// Fill the shelf rows the "Shelves full" check fails: a row window more than 5% empty gets
// another facing of a product already on it, one facing at a time, until the row is full enough
// or nothing more can go there. Air on a merged row is width no product covers, so moving or
// regrouping products never fills it: only more facings do.
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { rowWindowAirOf, type RowWindowAir } from "@CommonUtils/cdtStudio/domain/aestheticsScore";
import type { PlanVerdict } from "./planVerdict";
import type { GeneratedLayout, StudioItem, StudioRunGeometry } from "./studioTypes";
import { layoutFromItems } from "./verticalBlockingFix";
import { runBayWidthOf, slacksOf } from "./bayShare";

/** The check that fails a row with too much air. */
export const SHELVES_FULL_GATE = "shelves-not-full";

/** Most facings one fill adds; each is judged on the whole plan. */
const MAX_FILL_STEPS = 40;

/** A plan as the fill reads it: its products as drawn, and the tile's verdict. */
export interface FillPlan {
  pog: Pog;
  items: readonly StudioItem[];
  verdict: PlanVerdict;
}

export interface FillContext {
  runs: readonly StudioRunGeometry[];
  /** The plan's products as drawn, and its verdict, read once for each plan. */
  read: (pog: Pog) => FillPlan;
  /** The layout drawn the way the studio draws its plans. */
  stamp: (layout: GeneratedLayout, items: readonly StudioItem[]) => Pog;
  /** The block a product belongs to: the fill tries one product per block on each row. */
  blockOf: (skuId: string) => string;
  /** The candidate does no harm to the plan it was made from. */
  holds: (candidate: PlanVerdict, incumbent: PlanVerdict) => boolean;
}

/** Why a row stays short: every product on it is at its most facings, the gap left is
 *  narrower than any of its products, or each facing that would fit makes the plan worse. */
export type ShortRowReason = "maxFacings" | "noRoom" | "harm";

export interface ShortRow {
  run: number;
  shelf: number;
  bays: readonly number[];
  /** Uncovered width over the row's width, 0-1. */
  airShare: number;
}

export interface RowFill {
  /** The plan filled, or the start where nothing could be added. */
  end: FillPlan;
  /** Facings added, in the order they were added. */
  grown: readonly { skuId: string; run: number; shelf: number }[];
  /** Rows still over the bar at the end, each with why. */
  short: readonly (ShortRow & { reason: ShortRowReason })[];
}

const facingsOf = (item: StudioItem) =>
  item.placements.reduce((sum, placement) => sum + placement.hFacings, 0);

/** Days of supply now: the product that runs out first is grown first. */
const daysOfSupplyOf = (item: StudioItem): number =>
  item.dosPerFacing !== undefined
    ? item.dosPerFacing * facingsOf(item)
    : (item.dosDays ?? Number.POSITIVE_INFINITY);

/** Every row window over the check's bar, measured the way the check measures it. */
export const shortRowsOf = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  maxAirShare: number,
): RowWindowAir[] =>
  rowWindowAirOf(
    runs,
    items.flatMap((item) =>
      item.placements.map((placement) => ({
        run: placement.run,
        shelf: placement.shelf,
        x: placement.xCm,
        width: placement.widthCm,
      })),
    ),
  ).filter((window) => window.airShare > maxAirShare);

interface Candidate {
  skuId: string;
  index: number;
  run: number;
  shelf: number;
}

/** On each short row, for each block on it, the product that runs out first that can take one
 *  more facing within its max facings and the row's uncovered width. Rows with none say why. */
const candidatesOf = (
  items: readonly StudioItem[],
  windows: readonly RowWindowAir[],
  blockOf: (skuId: string) => string,
  runs: readonly StudioRunGeometry[],
): { candidates: Candidate[]; blocked: Map<RowWindowAir, ShortRowReason> } => {
  const candidates: Candidate[] = [];
  const blocked = new Map<RowWindowAir, ShortRowReason>();
  for (const window of windows) {
    const room = window.widthCm - window.coveredCm;
    const fit = slacksOf(
      runBayWidthOf(
        runs.find((run) => run.run === window.run),
        runs,
      ),
    ).fit;
    const onRow = items.flatMap((item) =>
      item.placements.flatMap((placement, index) =>
        placement.run === window.run &&
        placement.shelf === window.shelf &&
        placement.widthCm > 0 &&
        placement.xCm + placement.widthCm > window.xCm &&
        placement.xCm < window.xCm + window.widthCm
          ? [{ item, index, xCm: placement.xCm }]
          : [],
      ),
    );
    const belowMax = onRow.filter(
      ({ item }) => facingsOf(item) < (item.maxFacings ?? Number.POSITIVE_INFINITY),
    );
    const fitting = belowMax.filter(({ item }) => item.facingWidthCm <= room + fit);
    if (fitting.length === 0) {
      blocked.set(window, belowMax.length === 0 ? "maxFacings" : "noRoom");
      continue;
    }
    const firstOut = new Map<string, (typeof fitting)[number]>();
    for (const entry of fitting) {
      const block = blockOf(entry.item.skuId);
      const best = firstOut.get(block);
      if (
        !best ||
        daysOfSupplyOf(entry.item) < daysOfSupplyOf(best.item) ||
        (daysOfSupplyOf(entry.item) === daysOfSupplyOf(best.item) && entry.xCm < best.xCm)
      ) {
        firstOut.set(block, entry);
      }
    }
    for (const { item, index } of [...firstOut.values()].sort(
      (a, b) => daysOfSupplyOf(a.item) - daysOfSupplyOf(b.item) || a.xCm - b.xCm,
    )) {
      candidates.push({ skuId: item.skuId, index, run: window.run, shelf: window.shelf });
    }
  }
  return { candidates, blocked };
};

const grownLayout = (items: readonly StudioItem[], candidate: Candidate): GeneratedLayout => {
  const layout = layoutFromItems(items);
  const placement = layout.placements[candidate.skuId];
  return {
    ...layout,
    placements: {
      ...layout.placements,
      [candidate.skuId]: {
        ...placement,
        segments: placement.segments.map((segment, index) =>
          index === candidate.index ? { ...segment, hFacings: segment.hFacings + 1 } : segment,
        ),
      },
    },
  };
};

const shortRowOf = (window: RowWindowAir): ShortRow => ({
  run: window.run,
  shelf: window.shelf,
  bays: window.bays,
  airShare: window.airShare,
});

/** Moving blocks never fills a row's air, so this adds facings instead, never at the score's expense. */
export const fillShortRows = (start: FillPlan, context: FillContext): RowFill => {
  const maxAirShare = start.verdict.maxRowAir;
  if (maxAirShare === undefined || !start.verdict.failedGates.includes(SHELVES_FULL_GATE)) {
    return { end: start, grown: [], short: [] };
  }
  let current = start;
  const grown: { skuId: string; run: number; shelf: number }[] = [];
  for (let step = 0; step < MAX_FILL_STEPS; step += 1) {
    if (!current.verdict.failedGates.includes(SHELVES_FULL_GATE)) break;
    const windows = shortRowsOf(current.items, context.runs, maxAirShare);
    if (windows.length === 0) break;
    // The emptiest row first; a row where no facing holds gives way to the next.
    const blocked = new Map<RowWindowAir, ShortRowReason>();
    let best: { plan: FillPlan; candidate: Candidate } | null = null;
    for (const window of [...windows].sort((a, b) => b.airShare - a.airShare)) {
      const found = candidatesOf(current.items, [window], context.blockOf, context.runs);
      found.blocked.forEach((reason, at) => blocked.set(at, reason));
      for (const candidate of found.candidates) {
        const plan = context.read(
          context.stamp(grownLayout(current.items, candidate), current.items),
        );
        if (!context.holds(plan.verdict, current.verdict)) continue;
        if (!best || plan.verdict.score > best.plan.verdict.score) best = { plan, candidate };
      }
      if (best) break;
    }
    if (!best) {
      return {
        end: current,
        grown,
        short: windows.map((window) => ({
          ...shortRowOf(window),
          reason: blocked.get(window) ?? "harm",
        })),
      };
    }
    current = best.plan;
    grown.push({
      skuId: best.candidate.skuId,
      run: best.candidate.run,
      shelf: best.candidate.shelf,
    });
  }
  const left = current.verdict.failedGates.includes(SHELVES_FULL_GATE)
    ? shortRowsOf(current.items, context.runs, maxAirShare)
    : [];
  return {
    end: current,
    grown,
    short: left.map((window) => ({ ...shortRowOf(window), reason: "harm" as const })),
  };
};
